import {
  isReservedUsageEventField,
  USAGE_EVENT_FIELD_PATTERN,
} from '@monetize/constants';
import { getRequiredMessage } from '@monetize/types/common';
import * as z from 'zod';
import { UserRoleEnumZ } from './user.types';

export enum CustomFieldStatusEnum {
  ACTIVE = 'ACTIVE',
}
export const CustomFieldStatusEnumZ = z.enum(CustomFieldStatusEnum);

export enum CustomFieldPermissionsEnum {
  READ = 'READ',
  WRITE = 'WRITE',
}
export const CustomFieldPermissionsEnumZ = z.enum(CustomFieldPermissionsEnum);

export enum CustomFieldPermissionsUiEnum {
  HIDDEN = 'HIDDEN',
  EDITABLE = 'EDITABLE',
  READ_ONLY = 'READ_ONLY',
}
export const CustomFieldPermissionsUiEnumZ = z.enum(
  CustomFieldPermissionsUiEnum,
);

export enum CustomFieldAccessScopeEnum {
  ALL_ROLES = 'ALL_ROLES',
  SPECIFIC_ROLES = 'SPECIFIC_ROLES',
}
export const CustomFieldAccessScopeEnumZ = z.enum(CustomFieldAccessScopeEnum);

export const CustomFieldPermissionsConfigSchema = z.discriminatedUnion(
  'accessScope',
  [
    z.object({
      accessScope: z.literal(CustomFieldAccessScopeEnum.ALL_ROLES),
      permissions: z.array(CustomFieldPermissionsEnumZ),
    }),
    z.object({
      accessScope: z.literal(CustomFieldAccessScopeEnum.SPECIFIC_ROLES),
      permissionsByRole: z.partialRecord(
        UserRoleEnumZ,
        z.array(CustomFieldPermissionsEnumZ),
      ),
    }),
  ],
);
export type ICustomFieldPermissionsConfig = z.infer<
  typeof CustomFieldPermissionsConfigSchema
>;

export enum CustomFieldLifeCyclePolicyEnum {
  RETAIN_ON_AMENDMENT_QUOTE = 'RETAIN_ON_AMENDMENT_QUOTE',
  RETAIN_ON_RENEWAL_QUOTE = 'RETAIN_ON_RENEWAL_QUOTE',
  COPY_FROM_BLUEPRINT = 'COPY_FROM_BLUEPRINT',
}
export const CustomFieldLifeCyclePolicyEnumZ = z.enum(
  CustomFieldLifeCyclePolicyEnum,
);

export enum CustomFieldEntityEnum {
  ACCOUNT = 'ACCOUNT',
  BILL_GROUP = 'BILL_GROUP',
  CONTACT = 'CONTACT',
  CONTRACT = 'CONTRACT',
  CREDIT = 'CREDIT',
  CREDIT_NOTE = 'CREDIT_NOTE',
  INVOICE = 'INVOICE',
  OFFERING = 'OFFERING',
  PRODUCT = 'PRODUCT',
  QUOTE = 'QUOTE',
  QUOTE_OFFERING_GROUP = 'QUOTE_OFFERING_GROUP',
  RATE = 'RATE',
  LEGAL_ENTITY = 'LEGAL_ENTITY',
  USAGE_EVENT = 'USAGE_EVENT',
}
export const CustomFieldEntityEnumZ = z.enum(CustomFieldEntityEnum);

export enum CustomFieldTypeEnum {
  CHECKBOX = 'CHECKBOX',
  DATE = 'DATE',
  NUMBER = 'NUMBER',
  SINGLE_LINE_TEXT = 'SINGLE_LINE_TEXT',
  DROPDOWN = 'DROPDOWN',
  MULTI_SELECT = 'MULTI_SELECT',
}
export const CustomFieldTypeEnumZ = z.enum(CustomFieldTypeEnum);

export const CustomFieldValueSchema = z.object({
  enabled: z.boolean(),
  order: z.number(),
  label: z.string().min(1, { message: getRequiredMessage('label') }),
  value: z.string().min(1, { message: getRequiredMessage('value') }),
});

export type CustomFieldValue = z.infer<typeof CustomFieldValueSchema>;

export const CustomFieldReqSchema = z.object({
  entity: CustomFieldEntityEnumZ,
  status: CustomFieldStatusEnumZ,
  key: z
    .string()
    .min(1, getRequiredMessage('API Name'))
    .max(40, 'Must be 40 or fewer characters long.')
    .regex(/^[a-zA-Z0-9_]*$/, 'Must contain only alphanumeric and underscore.'),
  displayLabel: z
    .string()
    .min(1, getRequiredMessage('Label'))
    .max(60, 'Must be 60 or fewer characters long.'),
  description: z
    .string()
    .max(255, 'Must be 255 or fewer characters long.')
    .nullish(),
  tooltip: z.string().max(60, 'Must be 60 or fewer characters long.').nullish(),
  type: CustomFieldTypeEnumZ,
  // Only applicable for DROPDOWN type
  values: CustomFieldValueSchema.array().nullish(),
  /**
   * @deprecated Use permissionsConfig instead. Still accepted by the API for
   * back-compat (translated to an ALL_ROLES config), but the FE no longer sends it.
   */
  permissions: z.array(CustomFieldPermissionsEnumZ).nullish(),
  permissionsConfig: CustomFieldPermissionsConfigSchema.nullish(),
  lifeCyclePolicies: z.array(CustomFieldLifeCyclePolicyEnumZ).nullish(),
});
export type ICustomFieldReqSchema = z.infer<typeof CustomFieldReqSchema>;

/**
 * UI displays permissions differently than the API
 */
export const CustomFieldReqUiSchema = CustomFieldReqSchema.omit({
  permissions: true,
  permissionsConfig: true,
})
  .extend({
    accessScope: CustomFieldAccessScopeEnumZ,
    allRolesVisibility: CustomFieldPermissionsUiEnumZ,
    visibilityByRole: z.partialRecord(
      UserRoleEnumZ,
      CustomFieldPermissionsUiEnumZ,
    ),
  })
  .superRefine((data, ctx) => {
    // Usage event keys become usage-service property names, which have a
    // stricter identifier rule than other entities and a reserved-name list.
    if (data.entity === CustomFieldEntityEnum.USAGE_EVENT) {
      if (!USAGE_EVENT_FIELD_PATTERN.test(data.key)) {
        ctx.addIssue({
          code: 'custom',
          message:
            'Must start with a letter and contain only letters, digits, and underscores.',
          path: ['key'],
        });
      } else if (isReservedUsageEventField(data.key)) {
        ctx.addIssue({
          code: 'custom',
          message: `'${data.key}' is reserved by the usage service.`,
          path: ['key'],
        });
      }
    }
    if (
      data.accessScope === CustomFieldAccessScopeEnum.SPECIFIC_ROLES &&
      !Object.values(data.visibilityByRole ?? {}).some(
        (visibility) =>
          !!visibility && visibility !== CustomFieldPermissionsUiEnum.HIDDEN,
      )
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one role must have visibility',
        path: ['visibilityByRole'],
      });
    }
    if (
      (data.type === CustomFieldTypeEnum.DROPDOWN ||
        data.type === CustomFieldTypeEnum.MULTI_SELECT) &&
      !data.values?.length
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Values are required for dropdown fields',
        path: ['values'],
      });
    } else if (
      (data.type === CustomFieldTypeEnum.DROPDOWN ||
        data.type === CustomFieldTypeEnum.MULTI_SELECT) &&
      data.values?.every((item) => !item.enabled)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one value must be enabled',
        path: ['values'],
      });
    } else if (
      (data.type === CustomFieldTypeEnum.DROPDOWN ||
        data.type === CustomFieldTypeEnum.MULTI_SELECT) &&
      new Set(data.values?.map(({ value }) => value)).size !==
        (data.values?.length ?? 0)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Every value must be unique',
        path: ['values'],
      });
    } else if (
      data.type !== CustomFieldTypeEnum.DROPDOWN &&
      data.type !== CustomFieldTypeEnum.MULTI_SELECT &&
      !!data.values?.length
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Values are only allowed for dropdown and multi-select fields',
        path: ['values'],
      });
    }
  });

export type ICustomFieldReqUiSchema = z.infer<typeof CustomFieldReqUiSchema>;

// from GET /rules/rules/{id}
export const CustomFieldResSchema = z.object({
  id: z.string(),
  entity: CustomFieldEntityEnumZ,
  status: CustomFieldStatusEnumZ,
  key: z.string().min(1, getRequiredMessage('API Name')),
  displayLabel: z.string().min(1, getRequiredMessage('Label')),
  description: z.string().nullish(),
  tooltip: z.string().nullish(),
  type: CustomFieldTypeEnumZ,
  values: CustomFieldValueSchema.array().nullish(),
  /**
   * @deprecated Removed from the custom fields API responses. Use
   * permissionsConfig for the settings page and runningUserPermissions for
   * record forms. Kept optional only for back-compat with older responses.
   */
  permissions: z.array(CustomFieldPermissionsEnumZ).nullish(),
  permissionsConfig: CustomFieldPermissionsConfigSchema.nullish(),
  runningUserPermissions: z.array(CustomFieldPermissionsEnumZ).nullish(),
  lifeCyclePolicies: z.array(CustomFieldLifeCyclePolicyEnumZ).nullish(),
});

export type ICustomFieldResSchema = z.infer<typeof CustomFieldResSchema>;

export const CustomFieldListResSchema = CustomFieldResSchema.omit({ id: true });
export type CustomFieldListRes = z.infer<typeof CustomFieldListResSchema>;

// Custom field type that exists on each entity that supports custom fields
export const CustomFieldRecordSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]).nullish(),
);
export type ICustomFieldRecordSchema = z.infer<typeof CustomFieldRecordSchema>;

export type ICustomFieldMode = 'modal' | 'accordion' | 'drawer' | 'page';
